import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table'
import { useMemo } from 'react'
import { DataTableAggregationFns } from '../fns/aggregationFns'
import { DataTableFilterFns } from '../fns/filterFns'
import { DataTableSortingFns } from '../fns/sortingFns'
import { TableLocalizationEN } from '../locales/en'
import type {
  DataTableDefinedTableOptions,
  DataTableOptions,
  DataTableRowData,
} from '../types'

export const DataTableDefaultColumn = {
  maxSize: 1000,
  minSize: 40,
  size: 180,
} as const

export const DataTableDefaultDisplayColumn = {
  columnDefType: 'display',
  enableColumnDragging: false,
  enableColumnFilter: false,
  enableColumnOrdering: false,
  enableEditing: false,
  enableGlobalFilter: false,
  enableGrouping: false,
  enableHiding: false,
  enableResizing: false,
  enableSorting: false,
} as const

export const useCxTableOptions: <TData extends DataTableRowData>(
  tableOptions: DataTableOptions<TData>,
) => DataTableDefinedTableOptions<TData> = <TData extends DataTableRowData>({
  aggregationFns,
  autoResetExpanded = false,
  columnFilterDisplayMode = 'subheader',
  columnResizeDirection,
  columnResizeMode = 'onChange',

  defaultColumn,
  defaultDisplayColumn,
  editDisplayMode = 'cell',
  enableBatchRowSelection = true,
  enableBottomToolbar = true,
  enableColumnFilters = true,
  enableColumnOrdering = false,
  enableColumnPinning = true,
  enableColumnResizing = false,
  enableColumnVirtualization,
  enableExpanding,
  enableFacetedValues = false,
  enableFilters = true,
  enableGlobalFilter = true,
  enableGlobalFilterRankedResults = true,
  enableGrouping = false,
  enableHeaderActionsHoverReveal = false,
  enableHiding = true,
  enableMultiRowSelection = true,
  enableMultiSort = false,
  enablePagination = true,
  enableRowPinning = false,
  enableRowSelection = false,
  enableRowVirtualization,
  enableSelectAll = true,
  enableSorting = true,
  enableStickyHeader = true,
  enableTableFooter = true,
  enableTableHead = true,
  enableToolbarInternalActions = true,
  enableTopToolbar = true,
  filterFns,

  layoutMode,
  localization,
  manualFiltering,
  manualGrouping,
  manualPagination,
  manualSorting,
  positionCreatingRow = 'top',
  positionExpandColumn = 'first',
  positionGlobalFilter = 'left',
  positionPagination = 'bottom',
  rowPinningDisplayMode = 'sticky',
  selectAllMode = 'page',
  selectAllLabel = 'conversations',
  sortingFns,
  ...rest
}: DataTableOptions<TData>) => {
  localization = useMemo(
    () => ({
      ...TableLocalizationEN,
      ...localization,
    }),
    [localization],
  )
  aggregationFns = useMemo(() => ({ ...DataTableAggregationFns, ...aggregationFns }), [])
  filterFns = useMemo(() => ({ ...DataTableFilterFns, ...filterFns }), [])
  sortingFns = useMemo(() => ({ ...DataTableSortingFns, ...sortingFns }), [])
  defaultColumn = useMemo(() => ({ ...DataTableDefaultColumn, ...defaultColumn }), [defaultColumn])
  defaultDisplayColumn = useMemo(
    () => ({
      ...DataTableDefaultDisplayColumn,
      ...defaultDisplayColumn,
    }),
    [defaultDisplayColumn],
  )
  // cannot be changed after initialization
  ;[enableColumnVirtualization, enableRowVirtualization] = useMemo(
    () => [enableColumnVirtualization, enableRowVirtualization],
    [],
  )

  if (!columnResizeDirection) {
    columnResizeDirection = 'ltr'
  }

  layoutMode = layoutMode || (enableColumnResizing ? 'grid-no-grow' : 'semantic')
  if (layoutMode === 'semantic' && (enableRowVirtualization || enableColumnVirtualization)) {
    layoutMode = 'grid'
  }

  if (enableRowVirtualization) {
    enableStickyHeader = true
  }

  if (enablePagination === false && manualPagination === undefined) {
    manualPagination = true
  }

  if (!rest.data?.length) {
    manualFiltering = true
    manualGrouping = true
    manualPagination = true
    manualSorting = true
  }

  return {
    aggregationFns,
    autoResetExpanded,
    columnFilterDisplayMode,
    columnResizeDirection,
    columnResizeMode,
    defaultColumn,
    defaultDisplayColumn,
    editDisplayMode,
    enableBatchRowSelection,
    enableBottomToolbar,
    enableColumnFilters,
    enableColumnOrdering,
    enableColumnPinning,
    enableColumnResizing,
    enableColumnVirtualization,
    enableExpanding,
    enableFacetedValues,
    enableFilters,
    enableGlobalFilter,
    enableGlobalFilterRankedResults,
    enableGrouping,
    enableHeaderActionsHoverReveal,
    enableHiding,
    enableMultiRowSelection,
    enableMultiSort,
    enablePagination,
    enableRowPinning,
    enableRowSelection,
    enableRowVirtualization,
    enableSelectAll,
    enableSorting,
    enableStickyHeader,
    enableTableFooter,
    enableTableHead,
    enableToolbarInternalActions,
    enableTopToolbar,
    filterFns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: enableExpanding || enableGrouping ? getExpandedRowModel() : undefined,
    getFacetedMinMaxValues: enableFacetedValues ? getFacetedMinMaxValues() : undefined,
    getFacetedRowModel: enableFacetedValues ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: enableFacetedValues ? getFacetedUniqueValues() : undefined,
    getFilteredRowModel:
      enableColumnFilters || enableGlobalFilter || enableFilters
        ? getFilteredRowModel()
        : undefined,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getSubRows: (row) => row?.subRows,
    layoutMode,
    localization,
    manualFiltering,
    manualGrouping,
    manualPagination,
    manualSorting,
    positionCreatingRow,
    positionExpandColumn,
    positionGlobalFilter,
    positionPagination,
    rowPinningDisplayMode,
    selectAllLabel,
    selectAllMode,
    sortingFns,
    ...rest,
  } as DataTableDefinedTableOptions<TData>
}
